import { TransactionRoleSchema } from '../codecs/schema/formField';

/**
 * Extracts the first error message from a TanStack Form field.
 * Uses a structural type so it works with any FieldApi regardless of validator generics.
 */
export const getFormFieldErrorMsg = (field: {
  state: { meta: { errors: Array<unknown> } };
}): string | undefined => {
  const first = field.state.meta.errors[0];
  if (first === undefined || first === null) return undefined;
  if (typeof first === 'object' && first !== null && 'message' in first) {
    return (first as { message: string }).message;
  }
  return String(first);
};

export type ValidationInputValue =
  | { t: 'string'; value: string | undefined }
  | { t: 'checkbox'; value?: boolean }
  | { t: 'file'; fileId: string | undefined };

export type ValidationUser = {
  role?: TransactionRoleSchema | null;
  isAdmin: boolean;
};

/**
 * We wrap the validation status in an object so we can add details like error lists later without forcing callers to change.
 */
export type ValidationResult = {
  isValid: boolean;
};

type CheckboxRule = {
  id: string;
  rule: 'EXACTLY_N';
  n?: number | null;
};

/**
 * Minimal field metadata used to check permissions and whether a value can stay empty.
 * enteredBy narrows who is expected to fill the field; if it is empty the field must be prefilled or entered by admin.
 * isAdminAllowed lets admins step in on fields that normally belong to someone else so escalations can be unblocked.
 * isOptional marks fields that the business flow allows to be blank; required fields omit or false this flag.
 * checkboxGroup describes group-based rules (like exact counts) that only matter for checkbox sets; other fields ignore it.
 */
export type ValidationField = {
  enteredBy?: TransactionRoleSchema[] | null;
  isAdminAllowed?: boolean;
  isOptional?: boolean | null;
  formFieldId: string;
  checkboxGroup?: CheckboxRule | null;
};

/**
 * Returns the subset of fields that the user is allowed to fill, based on transaction role or admin override.
 *
 */
export const filterFieldsByUser = (params: {
  fields: ValidationField[];
  user: ValidationUser;
}): ValidationField[] => {
  const { fields, user } = params;

  return fields.filter((field) => {
    const hasRoleAccess = !!user.role && field.enteredBy?.includes(user.role);

    const hasAdminAccess = user.isAdmin && field.isAdminAllowed;

    return hasRoleAccess || hasAdminAccess;
  });
};

/**
 * Checks whether required fields are filled and checkbox rules are satisfied for the provided fields and values.
 * This does not look at user access; callers should pass only the fields that apply in their context.
 */
export const validateFieldValues = (params: {
  fields: ValidationField[];
  values: Record<string, ValidationInputValue>;
}): ValidationResult => {
  const { fields, values } = params;
  if (fields.length === 0) {
    return { isValid: true };
  }

  const isFieldFilled = (val: ValidationInputValue | undefined): boolean => {
    if (!val) return false;

    switch (val.t) {
      case 'string':
        return !!val.value && val.value.trim().length > 0;
      case 'checkbox':
        // Checkboxes are always implicitly filled. If the user sees a checkbox that is unchecked and wants it as false, it will leave it as it is, otherwise it checks it to true, so it is always filled.
        return true;
      case 'file':
        return !!val.fileId;
    }
  };

  const rulesById = fields.reduce<Record<string, CheckboxRule>>(
    (acc, field) =>
      field.checkboxGroup
        ? { ...acc, [field.checkboxGroup.id]: field.checkboxGroup }
        : acc,
    {}
  );

  const initial = { standaloneFields: [], checkboxSets: {} };
  const { standaloneFields, checkboxSets } = fields.reduce<{
    standaloneFields: ValidationField[];
    checkboxSets: Record<string, { rule: CheckboxRule; fieldIds: string[] }>;
  }>((acc, field) => {
    const rule = field.checkboxGroup?.id
      ? rulesById[field.checkboxGroup?.id]
      : undefined;
    if (!rule) {
      return { ...acc, standaloneFields: [...acc.standaloneFields, field] };
    }

    const existing = acc.checkboxSets[rule.id] ?? { rule, fieldIds: [] };
    return {
      ...acc,
      checkboxSets: {
        ...acc.checkboxSets,
        [rule.id]: {
          ...existing,
          fieldIds: [...existing.fieldIds, field.formFieldId]
        }
      }
    };
  }, initial);

  const hasEmptyRequiredStandaloneField = standaloneFields.some(
    (field) => !field.isOptional && !isFieldFilled(values[field.formFieldId])
  );

  const hasInvalidCheckboxGroup = Object.values(checkboxSets).some(
    ({ rule, fieldIds }) => {
      if (rule.rule !== 'EXACTLY_N') return false;

      const checkedCount = fieldIds.filter((id) => {
        const val = values[id];
        return val?.t === 'checkbox' && val.value === true;
      }).length;

      return checkedCount !== rule.n;
    }
  );

  return {
    isValid: !hasEmptyRequiredStandaloneField && !hasInvalidCheckboxGroup
  };
};

/**
 * Checks whether a form is valid and complete for the current user by only considering fields they can actually edit.
 * Returns true when all relevant required fields and checkbox rules are satisfied, or when no fields apply to this user.
 */
export const validateFieldsForUser = (params: {
  fields: ValidationField[];
  values: Record<string, ValidationInputValue>;
  user: ValidationUser;
}): ValidationResult => {
  const { fields, values, user } = params;

  const relevantFields = filterFieldsByUser({ fields, user });

  return validateFieldValues({ fields: relevantFields, values });
};
